<template>
  <div class="tender-options">
    <PageHeaderOptions
      :allowChangePIC="allowChangePIC"
      :allowCancel="allowCancel"
      :showSubstitute="allowResetToDraft"
      :previousPersonInCharge="currentPersonInCharge"
      :confirmCancelMsg="cancelMsg()"
      :itemName="tenderType()"
      :site="site"
      :ignoredSiteUsernames="[deep(currentTender, 'general.approver.username'), deep(currentTender, 'general.manager.username')]"
      @change-pic="val => changePIC(val)"
      @cancelItem="cancelTender()"
    >
      <li
        v-if="allowResetToDraft"
        slot="substitute"
      >
        <DialogSlot
          id="tender-reset-to-draft-dialog"
          ref="resetToDraft-modal"
          :modalBtnClass="'btn--transparent'"
          :modalBtn="$t('action.resetToDraft')"
        >
          <div
            slot="content"
            class="slot-content"
          >
            <span class="font-bold">{{ $t('hint.resetTenderToDraft') }}</span>
          </div>
          <div slot="buttons">
            <button
              type="button"
              class="btn btn--orange close-reveal-modal"
              @click="resetToDraft()"
              v-hkldreset
            >{{ $t('action.yesContinue') }}</button>
          </div>
        </DialogSlot>
      </li>
    </PageHeaderOptions>
  </div>
</template>

<script>
import PageHeaderOptions from '@/modules/PageHeaderOptions'
import DialogSlot from '@/controls/DialogSlot'
import userHelper from '@/utils/userHelper'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { tenderStatus } from '@/utils/locale'
import navigation from '@/utils/navigation'
import { ActionTypes } from '@/utils/consts'
import i18n from '@/utils/i18n'
import { utils as formUtils } from '@/plugin/InputChange'
import { updateGeneralFields } from '@/api/tenders/request'

export default {
  name: 'TenderOptions',
  components: {
    PageHeaderOptions,
    DialogSlot,
  },
  data () {
    return {
      editViewMap: {
        tenderDetails: 'tenderDetailsView',
        tenderAddendumDetails: 'tenderAddendumDetailsView',
      },
    }
  },

  methods: {
    ...mapActions('tenders', [
      'refreshTender',
      'updateTenderStatus',
    ]),
    ...mapMutations('tenders', {
      updateTenderField: 'TENDER_UPDATE_PATH_FIELD',
    }),

    closeModal (dialogRef) {
      if (dialogRef) {
        this.$refs[dialogRef].closeModal()
      }
    },
    async changePIC (newVal) {
      const prevPIC = this.currentTender.general.managedBy
      this.updateTenderField({
        path: ['general', 'managedBy'],
        newValue: newVal.id,
      })
      const tenderId = this.currentTender.general.id
      await updateGeneralFields(tenderId, { managedBy: newVal.id })
      await this.refreshTender()

      const pageToGo = this.editViewMap[this.$router.currentRoute.name]
      if (prevPIC === this.currentUser.id && pageToGo) {
        this.$router.replace({ name: pageToGo })
      }
    },

    async updateStatus (status, action, dialogRef) {
      await this.updateTenderStatus({
        id: this.currentTender.general.id,
        status,
        action,
      })
      this.closeModal(dialogRef)
    },

    async cancelTender () {
      formUtils.resetDirty()
      const action = ActionTypes.tenders.cancel
      await this.updateStatus(tenderStatus.CANCELLED, action)
      navigation.toHome()
    },
    async resetToDraft () {
      const action = this.isAddendum ? ActionTypes.tenders.addendum.reset : ActionTypes.tenders.reset
      await this.updateStatus(tenderStatus.DRAFT, action, 'resetToDraft-modal')
      this.refreshTender()
      const routerName = this.isAddendum ? 'tenderAddendumDetailsView' : 'tenderDetailsView'
      navigation.toPage(routerName, { id: this.currentTender.general.id })
    },
    cancelMsg () {
      return this.currentTender.general.tenderType === 'ITQ' ? i18n.t('hint.cancelITQ') : i18n.t('hint.cancelTender')
    },
    tenderType () {
      return this.currentTender.general.tenderType
    },
  },

  computed: {
    ...mapGetters('user', ['currentUser', 'isAdmin']),
    ...mapGetters('tenders', ['currentTender', 'isAddendum', 'isBeforeIssue']),
    canChangePIC () {
      return this.isAdmin || aclHelper.getAccess(aclPermsType.tenders.changePIC)
    },
    allowChangePIC () {
      return this.canChangePIC && this.isActiveItem
    },
    canResetToDraft () {
      return this.isAdmin || aclHelper.getAccess(aclPermsType.tenders.resetToDraft)
    },
    allowResetToDraft () {
      return this.canResetToDraft && this.isAbleResetStatus
    },
    personInCharge () {
      const general = this.currentTender.general || {}
      return (general.manager || {}).username
    },
    isPIC () {
      return this.personInCharge === this.currentUser.username
    },
    allowCancel () {
      return this.isPIC && !this.isAddendum && this.isBeforeIssue
    },
    isActiveItem () {
      const general = this.currentTender.general || {}
      const inactiveStatus = [ tenderStatus.CANCELLED, tenderStatus.CLOSED, tenderStatus.AWARDED, tenderStatus.REGRETTED ]
      return !(inactiveStatus.some(item => item === general.status))
    },
    site () {
      const general = this.currentTender.general || {}
      return general.site
    },
    isWappr () {
      const general = this.currentTender.general || {}
      return general.status === tenderStatus.WAPPR
    },
    isAppr () {
      const general = this.currentTender.general || {}
      return general.status === tenderStatus.APPR
    },
    isAbleResetStatus () {
      return this.isAppr || this.isWappr
    },
    deep () {
      return deep
    },
    currentPersonInCharge () {
      const username = deep(this.currentTender, 'general.manager.username')
      const displayName = deep(this.currentTender, 'general.manager.displayName')
      return `${userHelper.getUserName(username, displayName)} (${username})`
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/confirmCreate';

.tender-options {
  display: inline-block;
  height: 35px;
}
</style>

<style lang="scss">
.pic-slot-content {
  .multiselect__input {
    border-color: #cfcfcf;
    outline:none;
  }
  .multiselect__option {
    min-height: 30px;
  }
  .multiselect__content-wrapper {
    overflow-x: hidden;
    overflow-y: scroll;
    -ms-overflow-style: scrollbar;
  }
}
</style>
